import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useSharedState } from '../../hooks/SharedStateContext';
import { usePlanner } from '../../hooks/PlannerContext';
import './ActivityList.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRoute, faBriefcase, faCrown } from '@fortawesome/free-solid-svg-icons';

import { activityCategories, filterActivitiesByTargetLanguage } from '../../utilities/activities.ts';
import { getUserActivityAccess, getLastPlacementTest } from '../../utilities/firebase';
import { getFreeformStartActivity } from '../../utilities/activitySelection';
import { getUrlParameter } from '../../utilities/urlUtils';
import LearningProgressBar from './LearningProgressBar';

function ActivityList({ visible, onSelect }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    updateSelectedActivity,
    sharedState
  } = useSharedState();
  const plannerContext = usePlanner();
  const {
    currentPlannerData,
    isPlannerLoading,
    isPlannerPending,
    pendingPlannerObjective,
    handlePlannerClick: plannerClickHandler
  } = plannerContext || {};
  const [lastTestResult, setLastTestResult] = useState(null);
  const [hasCustomBucket, setHasCustomBucket] = useState(false);
  const [isCheckingCustomBucket, setIsCheckingCustomBucket] = useState(false);
  const [hasPitch, setHasPitch] = useState(false);

  useEffect(() => {
    // One users-doc read (cached after sign-in) covers both gated sections
    const checkActivityAccess = async () => {
      setIsCheckingCustomBucket(true);
      try {
        const { customBucket, hasPitch } = await getUserActivityAccess();
        setHasCustomBucket(!!customBucket);
        setHasPitch(hasPitch);
      } catch (error) {
        console.error('Error checking activity access:', error);
        setHasCustomBucket(false);
        setHasPitch(false);
      } finally {
        setIsCheckingCustomBucket(false);
      }
    };

    if (visible) {
      // Get cached placement test result (already fetched on auth in firebase.js)
      const cachedResult = getLastPlacementTest();
      setLastTestResult(cachedResult);
      checkActivityAccess();
    }
  }, [visible]);

  // Hide activity list if not visible OR if activity is pre-selected via URL (partner integration)
  if (!visible || getUrlParameter('activity')) return null;

  const handleActivityClick = (activity) => {
    updateSelectedActivity(activity.clickValue, true)
    onSelect(activity.clickValue)
  }

  const handleFreeformClick = () => {
    const activityValue = getFreeformStartActivity();
    updateSelectedActivity(activityValue, true);
    onSelect(activityValue);
  }

  const handlePlannerClick = () => {
    if (plannerClickHandler) {
      plannerClickHandler();
    }
  }

  const iconMap = {
    route: faRoute,
    briefcase: faBriefcase,
  }

  return (
    <div className="activity-buttons">
      {/* Learning objective section - show regardless of whether user has planner data */}
      <div className="planner-section">
        <h2>{t('continue_learning_objective')}</h2>

        {isPlannerPending || isPlannerLoading ? (
          // Show loading state while planner is being generated (from onboarding or manual creation)
          <div className="planner-card" role="status" aria-live="polite">
            <p className="planner-goal">
              <strong><span aria-hidden="true">⏳</span> {t('generating_learning_plan')}</strong>
            </p>
            {isPlannerPending && pendingPlannerObjective ? (
              <>
                <p className="planner-activity-count">
                  {t('planner_pending_objective', 'Creating plan for: "{{objective}}"', { objective: pendingPlannerObjective.substring(0, 50) + (pendingPlannerObjective.length > 50 ? '...' : '') })}
                </p>
                <p className="planner-activity-count planner-edit-hint">
                  {t('planner_edit_hint', "You'll be able to edit this goal anytime.")}
                </p>
              </>
            ) : (
              <p className="planner-activity-count">
                {t('planner_generation_message')}
              </p>
            )}
            <div className="planner-buttons">
              <button
                className="button activity planner-continue-button"
                disabled
                aria-disabled="true"
                aria-label={`${t('generating')}...`}
                style={{ opacity: 0.6, cursor: 'not-allowed' }}
              >
                <FontAwesomeIcon
                  icon={faRoute}
                  style={{ marginRight: '0.5em' }}
                  aria-hidden="true"
                />
                {t('generating')}...
              </button>
            </div>
          </div>
        ) : currentPlannerData && currentPlannerData.number_activities > 0 ? (
          // User has a learning plan - show progress and continue options
          <div className="planner-card">
            <p className="planner-goal">
              <strong>{t('your_learning_goal')}:</strong> {currentPlannerData.goal}
            </p>
            <p className="planner-activity-count">
              {currentPlannerData.number_activities} activities available
            </p>
            <LearningProgressBar 
              plannerData={currentPlannerData} 
              showLabel={false}
              className="compact"
            />
            <div className="planner-buttons">
              <button
                className="button activity planner-view-button"
                onClick={() => navigate('/objective')}
                aria-label={t('view_learning_plan')}
              >
                {t('view_learning_plan')}
              </button>
            </div>
          </div>
        ) : (
          // User doesn't have a learning plan - encourage them to set one up
          <div className="planner-card">
            <p className="planner-goal">
              {t('no_learning_goal_message')}
            </p>
            <div className="planner-buttons">
              <button
                className="button activity planner-continue-button continue-glow"
                onClick={handlePlannerClick}
                aria-label={t('set_your_learning_goal')}
              >
                <FontAwesomeIcon
                  icon={faRoute}
                  style={{ marginRight: '0.5em' }}
                  aria-hidden="true"
                />
                {t('set_your_learning_goal')}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className='grid-container'>
        <button
          className="button grid-item"
          onClick={handleFreeformClick}
          aria-label={t('activity_start_conversation')}
        >
          {t('activity_start_conversation')}
        </button>
        <button
          className="button grid-item"
          title={t('activity_tutorial')}
          onClick={() => handleActivityClick({ clickValue: "tutorial" })}
          aria-label={`${t('activity_tutorial')} (~15min)`}
        >
          <span aria-hidden="true">💡</span> {t('activity_tutorial')} (~15min)
        </button>
      </div>

      {Object.entries(activityCategories).map(([category, activities]) => {

        // Skip custom activities if user doesn't have custom_bucket
        if (category === 'activities_custom' && !hasCustomBucket) {
          return null;
        }

        // Skip pitch practice if user has no pitch saved
        if (category === 'activities_pitch' && !hasPitch) {
          return null;
        }

        // Filter activities based on target language
        const filteredActivities = filterActivitiesByTargetLanguage(activities, sharedState.targetLanguage);

        // Skip category if no activities remain after filtering
        if (filteredActivities.length === 0) {
          return null;
        }

        return (
          <div key={category}>
            <h2>
              {category === 'activities_custom'
                ? t('custom_activities_for_you')
                : t(`${category.toLowerCase()}`)
              }
              {category === 'activities_custom' && (
                <FontAwesomeIcon icon={faCrown} className="premium-badge" title={t('premium_feature')} />
              )}
              {category === 'activities_custom' && isCheckingCustomBucket && (
                <span style={{ fontSize: '0.8em', marginLeft: '10px' }}>{t('loading')}</span>
              )}
            </h2>
            <div className='grid-container'>
              {filteredActivities.map((activity, index) => {
                if (activity.clickValue === 'placement-test') {
                  return (
                    <button
                      className={`button activity grid-item ${activity.fullWidth ? 'full-width' : ''} ${activity.comingSoon ? 'disabled' : ''}`}
                      key={index}
                      title={activity.comingSoon ? t('activity_coming_soon') : activity.displayName}
                      onClick={() => !activity.comingSoon && handleActivityClick(activity)}
                      aria-label={lastTestResult
                        ? `${t('Try the level placement test again!')} - Last score: ${lastTestResult.score.toFixed(1)} out of 12`
                        : t(activity.displayName)
                      }
                      aria-disabled={activity.comingSoon ? 'true' : undefined}
                    >
                      {lastTestResult ? (
                        <>
                          {t('Try the level placement test again!')}
                          <div className="test-result-info">
                            {new Date(lastTestResult.date).toLocaleDateString()} -
                            Score: {lastTestResult.score.toFixed(1)} / 12
                          </div>
                        </>
                      ) : (
                        t(activity.displayName)
                      )}
                    </button>
                  );
                }

                return (
                  <button
                    className={`button activity grid-item ${activity.fullWidth ? 'full-width' : ''} ${activity.comingSoon ? 'disabled' : ''}`}
                    key={index}
                    title={activity.comingSoon ? t('activity_coming_soon') : activity.displayName}
                    onClick={() => !activity.comingSoon && handleActivityClick(activity)}
                    style={activity.color ? { backgroundColor: activity.color } : undefined}
                    aria-label={activity.comingSoon ? `${t(activity.displayName)} - ${t('activity_coming_soon')}` : t(activity.displayName)}
                    aria-disabled={activity.comingSoon ? 'true' : undefined}
                  >
                    {activity.icon && iconMap[activity.icon] && (
                      <FontAwesomeIcon
                        icon={iconMap[activity.icon]}
                        style={{ marginRight: '0.5em', marginBottom: '0.5em' }}
                        aria-hidden="true"
                      />
                    )}
                    {t(activity.displayName)}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ActivityList;
